import { useEffect, useRef, useState } from 'react';
import {
    DataGridProProps,
    GridColDef,
    GridColumnHeaderParams,
    GridPaginationModel,
    GridRenderCellParams,
    GridSortModel,
} from '@mui/x-data-grid-pro';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { Tooltip, Typography } from '@mui/material';

import { Pageable } from '../../../../../types/AxiosPageableResponse';
import { AppBySpendSummaryListCriteria as Criteria } from '../../../../../types/Analytics/AppBySpendSummaryListCriteria';
import { AppBySpendBySiteNameSummary as Spend } from '../../../../../types/Analytics/AppBySpendSummary';
import { ReportingCriteria } from '../../../../../types/Criteria/ReportingCriteria';
import ApiService from '../../../../../ApiService';
import DataGridPro from '../../../../../components/CDGridPro';
import Utils from '../../../../../components/Utils';

/**
 * Spend Data Grid
 */

type SpendByApplicationDataGridProps = Omit<DataGridProProps, 'columns' | 'rows'> & {
    rows?: Spend[];
};

const SpendByApplicationDataGrid = (props: SpendByApplicationDataGridProps) => {
    const columns: GridColDef[] = [
        {
            filterable: false,
            field: 'siteName',
            headerName: 'Site Name',
            flex: 1,
            width: 200,
            display: 'flex',
        },
        {
            filterable: false,
            field: 'totalCost',
            headerName: 'Spend',
            headerAlign: 'right',
            align: 'right',
            flex: 1,
            width: 200,
            display: 'flex',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Tooltip title="Total Spend" arrow>
                    <Typography variant="body2" fontWeight={500}>
                        {params.colDef.headerName}
                    </Typography>
                </Tooltip>
            ),
            renderCell: (params: GridRenderCellParams) => {
                const { row } = params;
                return <>{Utils.formatValue(row.totalCost, 'dollar')}</>;
            },
        },
        {
            filterable: false,
            field: 'impressions',
            headerName: 'Exposures',
            headerAlign: 'right',
            align: 'right',
            flex: 1,
            width: 200,
            display: 'flex',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Tooltip title="Total Exposures" arrow>
                    <Typography variant="body2" fontWeight={500}>
                        {params.colDef.headerName}
                    </Typography>
                </Tooltip>
            ),
            renderCell: (params: GridRenderCellParams) => {
                const { row } = params;
                return <>{Utils.formatValue(row.impressions)}</>;
            },
        },
    ];

    const getRowId = (row: Spend): string => {
        return row.siteName;
    };

    return (
        <DataGridPro
            // Overridable props
            paginationMode="server"
            sortingMode="server"
            {...props}
            disableColumnPinning
            getRowId={getRowId}
            columns={columns}
            slots={{
                toolbar: null,
            }}
        />
    );
};

/**
 * Spend Data Query Hook
 */

const fetch = async (criteria: Criteria): Promise<Pageable<Spend>> => {
    const { data } = await ApiService.getAppBySpendBySiteNameSummaryList(criteria, {
        headers: { XBusy: 'false' },
    });
    return data;
};

const useAppBySpendBySiteNameSummaryListQuery = (criteria: Criteria): UseQueryResult<Pageable<Spend>, unknown> => {
    return useQuery({
        queryKey: ['appBySpendBySiteNameSummaryList', criteria],
        queryFn: () => fetch(criteria),
        enabled: true,
    });
};

/**
 * Spend Data Grid Hook
 */

interface UseSpendByApplicationDataGridState {
    criteria: Criteria;
    setCriteria: (criteria: Criteria) => void;

    rows: Spend[];
    setRows: (rows: Spend[]) => void;
    paginationModel: GridPaginationModel;
    setPaginationModel: (paginationModel: GridPaginationModel) => void;
    sortModel: GridSortModel;
    setSortModel: (sortModel: GridSortModel) => void;

    query: UseQueryResult<Pageable<Spend>, unknown>;
}

interface UseSpendByApplicationDataGridProps {
    reportingCriteria: ReportingCriteria;
}

const getDefaultCriteria = (): Criteria => {
    return {
        agencyIds: [],
        dealerIds: [],
        startDate: new Date(),
        endDate: new Date(),
        mediaType: '',
        strategyType: '',

        page: 0,
        pageSize: 20,
        sortBy: 'totalCost',
        sort: 'desc',
    };
};

const useSpendByApplicationDataGrid = (
    props: UseSpendByApplicationDataGridProps
): UseSpendByApplicationDataGridState => {
    const { reportingCriteria } = props;

    const suppressPagination = useRef<boolean>(false);

    const defaultCriteria: Criteria = getDefaultCriteria();
    const [criteria, setCriteria] = useState<Criteria>({
        ...defaultCriteria,
        ...reportingCriteria,
    });

    const [rows, setRows] = useState<Spend[]>([]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: criteria.page,
        pageSize: criteria.pageSize,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>((): GridSortModel => {
        return [
            {
                field: criteria.sortBy || (defaultCriteria.sortBy as string),
                sort: criteria.sort === 'desc' ? 'desc' : 'asc',
            },
        ];
    });

    const query = useAppBySpendBySiteNameSummaryListQuery(criteria);
    const { data } = query;

    useEffect(() => {
        setCriteria({ ...criteria, ...reportingCriteria, page: 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportingCriteria]);

    useEffect(() => {
        setRows(data?.content || []);
    }, [data]);

    useEffect(() => {
        if (suppressPagination.current === false) {
            setCriteria({ ...criteria, page: paginationModel.page, pageSize: paginationModel.pageSize });
        }

        suppressPagination.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);

    useEffect(() => {
        suppressPagination.current = true;
        setPaginationModel({ ...paginationModel, page: 0 });

        if (sortModel.length > 0) {
            setCriteria({
                ...criteria,
                sortBy: sortModel[0].field,
                sort: sortModel[0].sort === 'desc' ? 'desc' : 'asc',
                page: 0,
            });
        } else {
            setCriteria({
                ...criteria,
                sortBy: defaultCriteria.sortBy,
                sort: defaultCriteria.sort === 'desc' ? 'desc' : 'asc',
                page: 0,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortModel]);

    return {
        criteria,
        setCriteria,

        rows,
        setRows,
        paginationModel,
        setPaginationModel,
        sortModel,
        setSortModel,

        query,
    };
};

export type { SpendByApplicationDataGridProps };
export default SpendByApplicationDataGrid;

export { useAppBySpendBySiteNameSummaryListQuery };

export type { UseSpendByApplicationDataGridState, UseSpendByApplicationDataGridProps };
export { getDefaultCriteria };
export { useSpendByApplicationDataGrid };
